import client from './client';
import { User, PaginatedResponse, Role } from '../types';

export interface UserListParams {
  page?: number;
  limit?: number;
  role?: Role | 'all';
  include_inactive?: boolean;
}

export const getList = async (params: UserListParams = {}): Promise<PaginatedResponse<User>> => {
  const qs: Record<string, unknown> = {
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  };
  if (params.role && params.role !== 'all') qs.role = params.role;
  if (params.include_inactive) qs.include_inactive = true;
  const res = await client.get('/api/v1/users', { params: qs });
  return res.data;
};

export const getMe = async (): Promise<User> => {
  const res = await client.get('/api/v1/users/me');
  return res.data;
};

export const changeMyNickname = async (nickname: string): Promise<User> => {
  const res = await client.patch('/api/v1/users/me/nickname', { nickname });
  return res.data;
};

export const changeMyPassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  await client.patch('/api/v1/users/me/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
};

export const changeMyPhone = async (newPhone: string): Promise<User> => {
  const res = await client.patch('/api/v1/users/me/phone', { new_phone: newPhone });
  return res.data;
};

export const deactivateMe = async (password: string): Promise<void> => {
  await client.post('/api/v1/users/me/deactivate', { password });
};

export const createUser = async (data: {
  phone: string;
  password: string;
  name: string;
  car_number?: string;
  nickname?: string;
  company?: string;
  user_type?: string;
}): Promise<User> => {
  const res = await client.post('/api/v1/users', data);
  return res.data;
};

export const deactivateUser = async (id: string): Promise<User> => {
  const res = await client.delete(`/api/v1/users/${id}`);
  return res.data;
};

export const reactivateUser = async (id: string): Promise<User> => {
  const res = await client.post(`/api/v1/users/${id}/reactivate`);
  return res.data;
};

export const hardDeleteUser = async (id: string): Promise<void> => {
  await client.delete(`/api/v1/users/${id}/permanent`);
};

export const resetUserPassword = async (id: string, newPassword: string): Promise<User> => {
  const res = await client.patch(`/api/v1/users/${id}/password`, { new_password: newPassword });
  return res.data;
};

export const promoteUser = async (
  id: string,
  role: Role,
  apartment_ids: string[] = []
): Promise<User> => {
  const res = await client.post(`/api/v1/users/${id}/promote`, { role, apartment_ids });
  return res.data;
};

export const demoteUser = async (id: string): Promise<User> => {
  const res = await client.post(`/api/v1/users/${id}/demote`, { target_role: 'user' });
  return res.data;
};

export const getUserApartments = async (
  id: string
): Promise<{ user_id: string; apartment_ids: string[] }> => {
  const res = await client.get(`/api/v1/users/${id}/apartments`);
  return res.data;
};

export const setUserApartments = async (
  id: string,
  apartment_ids: string[]
): Promise<{ user_id: string; apartment_ids: string[] }> => {
  const res = await client.put(`/api/v1/users/${id}/apartments`, { apartment_ids });
  return res.data;
};
